// src/pages/Holidays.js
import React, { useMemo, useState, useEffect } from "react";
import HrLayout from "../../layouts/HrLayout";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import { FiPlus, FiDownload, FiTrash2, FiEdit, FiCalendar, FiSearch } from "react-icons/fi";
import { format, parseISO, isAfter, isBefore, compareAsc } from "date-fns";

// react-big-calendar
import { Calendar as BigCalendar, dateFnsLocalizer } from "react-big-calendar";
import formatFn from "date-fns/format";
import parseFn from "date-fns/parse";
import startOfWeekFn from "date-fns/startOfWeek";
import getDayFn from "date-fns/getDay";
import "react-big-calendar/lib/css/react-big-calendar.css";

// export helpers
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";

const locales = { "en-US": require("date-fns/locale/en-US") };
const localizer = dateFnsLocalizer({
  format: formatFn,
  parse: parseFn,
  startOfWeek: startOfWeekFn,
  getDay: getDayFn,
  locales,
});

// --- Helper small components inside the same file for simplicity ---

function AddHolidayModal({ open, onClose, onSave }) {
  const [form, setForm] = useState({
    name: "",
    date: "",
    type: "National",
    description: "",
    departments: "All",
    repeatYearly: false,
  });
  const [file, setFile] = useState(null);

  useEffect(() => {
    if (!open) {
      setForm({
        name: "",
        date: "",
        type: "National",
        description: "",
        departments: "All",
        repeatYearly: false,
      });
      setFile(null);
    }
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl bg-white rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Add Holiday</h3>
          <button className="text-gray-600" onClick={onClose}>✕</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Holiday Name"
            className="p-2 border rounded"
          />
          <input
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            type="date"
            className="p-2 border rounded"
          />

          <select
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
            className="p-2 border rounded"
          >
            <option>National</option>
            <option>Company</option>
            <option>Optional</option>
          </select>

          <select
            value={form.departments}
            onChange={(e) => setForm({ ...form, departments: e.target.value })}
            className="p-2 border rounded"
          >
            <option>All</option>
            <option>IT</option>
            <option>HR</option>
            <option>Finance</option>
            <option>Marketing</option>
          </select>

          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Description (optional)"
            className="p-2 border rounded col-span-1 md:col-span-2"
          />

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.repeatYearly}
              onChange={(e) => setForm({ ...form, repeatYearly: e.target.checked })}
            />
            Repeat every year
          </label>

          <div>
            <label className="block text-sm mb-1">Attach circular (PDF)</label>
            <input
              type="file"
              accept=".pdf,.png,.jpg"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            {file && <div className="text-xs mt-1 text-gray-600">{file.name}</div>}
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button className="bg-gray-200 text-gray-800" onClick={onClose}>Cancel</Button>
          <Button
            className="bg-blue-600 text-white"
            onClick={() => {
              if (!form.name || !form.date) {
                alert("Please enter name and date.");
                return;
              }
              // create object and pass file
              onSave({ ...form, attachedFile: file });
            }}
          >
            Save Holiday
          </Button>
        </div>
      </div>
    </div>
  );
}

function DetailsDrawer({ open, onClose, holiday }) {
  if (!open || !holiday) return null;
  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1" onClick={onClose} />
      <div className="w-96 bg-white shadow-xl p-6 overflow-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">{holiday.name}</h3>
          <button onClick={onClose}>✕</button>
        </div>

        <p className="text-sm text-gray-600 mb-3">{holiday.description || "No description"}</p>

        <div className="space-y-2">
          <div><strong>Date:</strong> {format(parseISO(holiday.date), "dd/MM/yyyy")}</div>
          <div><strong>Day:</strong> {format(parseISO(holiday.date), "EEEE")}</div>
          <div><strong>Type:</strong> {holiday.type}</div>
          <div><strong>Applies To:</strong> {holiday.departments}</div>
          <div><strong>Repeat yearly:</strong> {holiday.repeatYearly ? "Yes" : "No"}</div>
          {holiday.attachedFile && (
            <div className="mt-3">
              <strong>Attachment:</strong>
              <div className="mt-2">
                <a
                  href={holiday.attachedFile.url || "#"}
                  className="inline-flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded"
                  target="_blank"
                  rel="noreferrer"
                >
                  <FiDownload /> Download circular
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ConfirmDialog({ open, onCancel, onConfirm, title, desc }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg">
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-4">{desc}</p>
        <div className="flex justify-end gap-3">
          <Button className="bg-gray-200" onClick={onCancel}>Cancel</Button>
          <Button className="bg-red-600 text-white" onClick={onConfirm}>Delete</Button>
        </div>
      </div>
    </div>
  );
}

// --- Main Page ---
export default function Holidays() {
  // seed sample data
  const seed = [
    {
      id: 1,
      name: "New Year",
      date: "2025-12-31", // sample format YYYY-MM-DD
      type: "National",
      description: "Start the year celebration",
      departments: "All",
      repeatYearly: true,
      attachedFile: null,
    },
    {
      id: 2,
      name: "Independence Day",
      date: "2026-08-14",
      type: "National",
      description: "",
      departments: "All",
      repeatYearly: true,
      attachedFile: null,
    },
    {
      id: 3,
      name: "Company Foundation Day",
      date: "2026-11-04",
      type: "Company",
      description: "Company anniversary",
      departments: "All",
      repeatYearly: true,
      attachedFile: null,
    },
    {
      id: 4,
      name: "Christmas",
      date: "2026-12-24",
      type: "Optional",
      description: "Holiday for celebration",
      departments: "All",
      repeatYearly: true,
      attachedFile: null,
    },
  ];

  const [holidays, setHolidays] = useState(() =>
    seed.map((h) => ({ ...h, date: h.date }))
  );

  // UI state
  const [viewMode, setViewMode] = useState("list"); // list | calendar
  const [modalOpen, setModalOpen] = useState(false);
  const [drawerHoliday, setDrawerHoliday] = useState(null);
  const [confirmState, setConfirmState] = useState({ open: false, id: null });
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("All");
  const [filterUpcoming, setFilterUpcoming] = useState(false);
  const [sortBy, setSortBy] = useState("dateAsc"); // dateAsc/dateDesc/nameAsc/nameDesc/type

  // helper: attach file by creating object URL (in real app you would upload to server)
  const attachFileToHoliday = async (holiday, file) => {
    if (!file) return holiday;
    const url = URL.createObjectURL(file);
    return { ...holiday, attachedFile: { name: file.name, url } };
  };

  // create id generator
  const nextId = useMemo(() => {
    return () => Math.max(0, ...holidays.map((h) => h.id)) + 1;
  }, [holidays]);

  // Add holiday handler
  const handleSaveHoliday = async (payload) => {
    let newHoliday = {
      id: nextId(),
      name: payload.name,
      date: payload.date,
      type: payload.type,
      description: payload.description,
      departments: payload.departments,
      repeatYearly: payload.repeatYearly,
      attachedFile: null,
    };
    if (payload.attachedFile) {
      newHoliday = await attachFileToHoliday(newHoliday, payload.attachedFile);
    }
    setHolidays((s) => [...s, newHoliday]);
    setModalOpen(false);
  };

  // delete
  const doDelete = (id) => {
    setHolidays((s) => s.filter((h) => h.id !== id));
    setConfirmState({ open: false, id: null });
  };

  // export CSV / Excel
  const exportExcel = () => {
    const wsData = holidays.map((h) => ({
      Name: h.name,
      Date: h.date,
      Day: format(parseISO(h.date), "EEEE"),
      Type: h.type,
      Departments: h.departments,
      RepeatYearly: h.repeatYearly ? "Yes" : "No",
    }));
    const ws = XLSX.utils.json_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Holidays");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), "holidays.xlsx");
  };

  // simple PDF export (table)
  const exportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14);
    doc.text("Holiday Calendar", 14, 16);
    let y = 26;
    holidays.forEach((h, i) => {
      doc.setFontSize(11);
      doc.text(`${i + 1}. ${h.name} — ${format(parseISO(h.date), "dd/MM/yyyy")} (${format(parseISO(h.date), "EEEE")}) — ${h.type}`, 14, y);
      y += 8;
      if (y > 180) {
        doc.addPage();
        y = 20;
      }
    });
    doc.save("holidays.pdf");
  };

  // filtered + searched list
  const filtered = holidays
    .filter((h) => {
      if (filterType !== "All" && h.type !== filterType) return false;
      if (filterUpcoming) {
        // show only upcoming: date strictly after today
        const today = new Date();
        return isAfter(parseISO(h.date), today) || format(parseISO(h.date), "yyyy") === format(new Date(), "yyyy");
      }
      if (search) {
        const q = search.toLowerCase();
        return (
          h.name.toLowerCase().includes(q) ||
          h.type.toLowerCase().includes(q) ||
          (h.description || "").toLowerCase().includes(q)
        );
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === "dateAsc") return compareAsc(parseISO(a.date), parseISO(b.date));
      if (sortBy === "dateDesc") return compareAsc(parseISO(b.date), parseISO(a.date));
      if (sortBy === "nameAsc") return a.name.localeCompare(b.name);
      if (sortBy === "nameDesc") return b.name.localeCompare(a.name);
      if (sortBy === "type") return a.type.localeCompare(b.type);
      return 0;
    });

  // upcoming holiday (closest future)
  const upcoming = useMemo(() => {
    const today = new Date();
    const futures = holidays.filter((h) => isAfter(parseISO(h.date), today));
    if (!futures.length) return null;
    futures.sort((a, b) => compareAsc(parseISO(a.date), parseISO(b.date)));
    return futures[0];
  }, [holidays]);

  // events for calendar
  const events = holidays.map((h) => ({
    id: h.id,
    title: `${h.name} (${h.type})`,
    start: new Date(h.date),
    end: new Date(h.date),
    allDay: true,
    resource: h,
  }));

  return (
    <HrLayout>
      <div className="p-6 bg-gray-50 min-h-screen">

        {/* Top header + upcoming */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-1">Holiday Calendar</h1>
            <p className="text-sm text-gray-600">Manage company holidays, calendar view, export & announcements.</p>

            {upcoming && (
              <div className="mt-4 p-4 rounded-lg bg-gradient-to-r from-yellow-50 to-white border shadow-sm">
                <span className="text-sm text-gray-600">Next holiday</span>
                <div className="flex items-center gap-4 mt-1">
                  <div>
                    <div className="text-lg font-semibold">{upcoming.name}</div>
                    <div className="text-sm text-gray-600">
                      {format(parseISO(upcoming.date), "dd MMM yyyy")} — {format(parseISO(upcoming.date), "EEEE")}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col items-end gap-3">
            <div className="flex gap-2">
              <Button className="bg-white border" onClick={() => setViewMode(viewMode === "list" ? "calendar" : "list")}>
                <FiCalendar /> {viewMode === "list" ? "Calendar View" : "List View"}
              </Button>

              <Button className="bg-blue-600 text-white" onClick={() => setModalOpen(true)}>
                <FiPlus /> Add Holiday
              </Button>
            </div>

            <div className="flex gap-2">
              <Button className="bg-white border" onClick={exportExcel}><FiDownload /> Excel</Button>
              <Button className="bg-white border" onClick={exportPDF}><FiDownload /> PDF</Button>
            </div>
          </div>
        </div>

        {/* Filters / Search / Sort */}
        <Card className="p-4 mb-6">
          <div className="grid md:grid-cols-4 gap-3 items-center">
            <div className="flex items-center gap-2 border p-2 rounded">
              <FiSearch />
              <input
                placeholder="Search holidays..."
                className="outline-none w-full"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <select className="p-2 border rounded" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="All">All Types</option>
              <option value="National">National</option>
              <option value="Company">Company</option>
              <option value="Optional">Optional</option>
            </select>

            <label className="flex items-center gap-2">
              <input type="checkbox" checked={filterUpcoming} onChange={(e) => setFilterUpcoming(e.target.checked)} />
              Upcoming only
            </label>

            <select className="p-2 border rounded" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="dateAsc">Sort: Date ↑</option>
              <option value="dateDesc">Sort: Date ↓</option>
              <option value="nameAsc">Sort: Name A→Z</option>
              <option value="nameDesc">Sort: Name Z→A</option>
              <option value="type">Sort: Type</option>
            </select>
          </div>
        </Card>

        {/* Main content */}
        {viewMode === "list" ? (
          <Card className="p-4">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-orange-50 text-orange-600">
                    <th className="p-3 rounded-tl">Holiday</th>
                    <th className="p-3">Date</th>
                    <th className="p-3">Day</th>
                    <th className="p-3">Type</th>
                    <th className="p-3">Applies To</th>
                    <th className="p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((h) => (
                    <tr key={h.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-semibold">{h.name}</td>
                      <td className="p-3">{format(parseISO(h.date), "dd/MM/yyyy")}</td>
                      <td className="p-3">{format(parseISO(h.date), "EEEE")}</td>
                      <td className="p-3">
                        <span className={`px-3 py-1 rounded-full text-xs ${h.type === "National" ? "bg-green-100 text-green-700" : h.type === "Company" ? "bg-blue-100 text-blue-700" : "bg-yellow-100 text-yellow-700"}`}>
                          {h.type}
                        </span>
                      </td>
                      <td className="p-3">{h.departments}</td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <Button className="bg-yellow-400" onClick={() => setDrawerHoliday(h)}><FiEdit /> Details</Button>
                          <Button className="bg-red-500 text-white" onClick={() => setConfirmState({ open: true, id: h.id })}><FiTrash2 /> Delete</Button>
                          {h.attachedFile && h.attachedFile.url && (
                            <a href={h.attachedFile.url} target="_blank" rel="noreferrer" className="inline-flex items-center px-3 py-1 border rounded bg-white">
                              <FiDownload /> Circular
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-6 text-center text-gray-500">No holidays found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        ) : (
          <Card className="p-4">
            <BigCalendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 600 }}
              selectable
              views={["month", "week", "day"]}
              onSelectEvent={(event) => setDrawerHoliday(event.resource)}
              onSelectSlot={(slotInfo) => {
                // quick add on calendar slot
                const dateStr = format(slotInfo.start, "yyyy-MM-dd");
                setModalOpen(true);
                // pre-fill could be implemented by passing props
              }}
            />
          </Card>
        )}

        {/* Modal / Drawer / Confirm */}
        <AddHolidayModal open={modalOpen} onClose={() => setModalOpen(false)} onSave={handleSaveHoliday} />
        <DetailsDrawer open={!!drawerHoliday} holiday={drawerHoliday} onClose={() => setDrawerHoliday(null)} />
        <ConfirmDialog
          open={confirmState.open}
          title="Delete holiday"
          desc="Are you sure you want to delete this holiday? This action cannot be undone."
          onCancel={() => setConfirmState({ open: false, id: null })}
          onConfirm={() => doDelete(confirmState.id)}
        />
      </div>
    </HrLayout>
  );
}
